<script lang="ts">
    // Types
    import type { SubtitleLink } from '~/jet/models';

    // Shared Components
    import ShelfListItem from '@amp/web-app-components/src/components/lockups/ShelfListItem/ShelfListItem.svelte';
    import LineClamp from '@amp/web-app-components/src/components/LineClamp/LineClamp.svelte';
    import Chevron from '@amp/web-app-components/assets/icons/chevron-right-bold.svg';

    // Local Components
    import ClickAction from '~/components/jet/action/ClickAction.svelte';
    import DefaultFallback from '~/components/jet/Artwork/fallbacks/classical-symbol-square.svelte';
    import WorkFallback from '~/components/jet/Artwork/fallbacks/classical-works-square.svelte';
    import ProductContextMenu from '~/components/jet/ProductContextMenu.svelte';
    import InteractivePlayButton from '~/components/jet/buttons/InteractivePlayButton.svelte';

    // Utils/Actions
    import { handleClickAction } from '~/actions/click-action';
    import { getRafQueue } from '@amp/web-app-components/src/utils/rafQueue';
    import {
        makePausePlaybackAction,
        makeResumePlaybackAction,
    } from '~/actions/playback-actions';

    // Stores
    import { getI18n } from '~/stores/i18n';
    import SeparatedList from '@amp/web-app-components/src/components/helpers/SeparatedList.svelte';

    const i18n = getI18n();
    const rafQueue = getRafQueue();

    // Max number of title + subtitle lines allowed in different types of this lockup
    const TRACK_MAX_LINES_TOTAL = 2;
    const WORK_MAX_LINES_TOTAL = 3;
    const DEFAULT_MAX_LINES_TOTAL = 5;

    export let item;

    // Number of lines that title is wrapping to, used to compute how much extra space we have for subtitle
    let titleMultilineCount = 1;

    $: clickAction = item.segue;
    $: artwork = item.artwork;
    $: title = item.title;
    $: subtitle = item.subtitle;
    $: subtitleLinks = item.subtitleLinks;
    $: headline = item.headline;
    $: actionText = item.actionText;

    $: isFromAlbum = item.isFromAlbum;
    $: isTrackType = item.type === 'track';
    $: isWorkType = item.type === 'work';
    $: isRecordingType = item.type === 'recording';
    $: isDefaultType = !isTrackType && !isWorkType; // Albums and recordings
    $: hasHeader = !!item.header;

    $: artworkProfile = item.artworkProfile;
    $: artworkFallbackComponent = isWorkType ? WorkFallback : DefaultFallback;

    $: itemWithUpdatedPlaybackActions = {
        ...item,
        pauseAction: makePausePlaybackAction(),
        resumeAction: makeResumePlaybackAction(),
    };

    $: totalClampLines = (() => {
        if (isTrackType) {
            return TRACK_MAX_LINES_TOTAL;
        } else if (isWorkType) {
            return WORK_MAX_LINES_TOTAL;
        } else {
            return DEFAULT_MAX_LINES_TOTAL;
        }
    })();

    $: subtitleClampLines = Math.min(totalClampLines - titleMultilineCount, 2);

    // Schedule multiline updates with RAF queue
    $: updateMultilineTitle = (e: CustomEvent) => {
        rafQueue.add(() => (titleMultilineCount = e.detail.multilineCount));
    };

    function handleSubtitleClick(e: MouseEvent, link: SubtitleLink): void {
        handleClickAction(e, link?.segue || item.segue);
    }
</script>

<div
    class="shelf-list-item-classical"
    data-testid="shelf-list-item-classical"
    class:shelf-list-item-classical--default={isDefaultType}
    class:shelf-list-item-classical--from-album={isFromAlbum}
    class:shelf-list-item-classical--track={isTrackType}
    class:shelf-list-item-classical--work={isWorkType}
>
    <ShelfListItem
        {artwork}
        {title}
        {subtitle}
        {headline}
        {artworkProfile}
        {hasHeader}
        isPlayable={!!item.playAction}
    >
        <svelte:fragment slot="primary-action">
            <ClickAction action={clickAction} ariaLabel={title} />
        </svelte:fragment>

        <div class="secondary-actions" slot="secondary-actions">
            {#if clickAction}
                {#if actionText}
                    <span
                        class="secondary-actions__text"
                        data-testid="shelf-list-item-secondary-action-text"
                        >{actionText}</span
                    >
                {/if}
                <span
                    class="secondary-actions__icon"
                    data-testid="shelf-list-item-secondary-action-chevron"
                >
                    <Chevron class="chevron" aria-hidden="true" />
                </span>
            {:else if item?.contextMenuAction}
                <ProductContextMenu
                    contextMenuAction={item?.contextMenuAction}
                    buttonStyle="non-platter"
                />
            {/if}
        </div>

        <svelte:fragment slot="play-button">
            {#if item.playAction}
                <InteractivePlayButton
                    item={itemWithUpdatedPlaybackActions}
                    isExplicit={false}
                />
            {/if}
        </svelte:fragment>

        <svelte:component this={artworkFallbackComponent} slot="fallback" />

        <div class="shelf-list-item__header" slot="header">
            {#if item.header}
                <ClickAction action={item.header?.segue}>
                    <span
                        class="shelf-list-item__header-text"
                        dir="auto"
                        data-testid="shelf-list-item-header-title"
                    >
                        {item.header.title}
                    </span>
                    {#if item.header?.segue}
                        <Chevron class="chevron" aria-hidden="true" />
                    {/if}
                </ClickAction>
            {/if}
        </div>

        <svelte:fragment slot="title">
            <LineClamp
                clamp={totalClampLines - 1}
                allowFocusOverflow={true}
                on:resize={updateMultilineTitle}
                observe={true}
            >
                <ClickAction action={clickAction}>
                    {title}
                </ClickAction>
            </LineClamp>
        </svelte:fragment>

        <svelte:fragment slot="subtitle">
            <LineClamp clamp={subtitleClampLines} allowFocusOverflow={true}>
                {#if subtitleLinks && subtitleLinks.length > 0}
                    <SeparatedList
                        items={subtitleLinks}
                        translateFn={$i18n.t}
                        let:item={link}
                    >
                        {#if link.href}
                            <a
                                href={link.href}
                                class:link={link.segue}
                                data-testid="shelf-list-item-subtitle-link"
                                on:click|stopPropagation={(e) =>
                                    handleSubtitleClick(e, link)}
                            >
                                {link.title}
                            </a>
                        {:else if link.segue}
                            <button
                                data-testid="shelf-list-item-subtitle-button"
                                class="shelf-list-item__subtitle--button link"
                                on:click|stopPropagation={(e) =>
                                    handleSubtitleClick(e, link)}
                            >
                                <!--
                                    Workaround for rdar://100779462 (Regression : Library albums with multiple artists should show ellipsis in line2)
                                    `-webkit-line-clamp` fails when applied to
                                    <button>, but will still apply to contents
                                    wrapped in <span>.
                                -->
                                <span>{link.title}</span>
                            </button>
                        {:else}
                            <span data-testid="shelf-list-item-subtitle-text">
                                {link.title}
                            </span>
                        {/if}
                    </SeparatedList>
                {:else}
                    {subtitle || ''}
                {/if}
            </LineClamp>
        </svelte:fragment>
    </ShelfListItem>
</div>

<style lang="scss">
    @use '@amp/web-shared-styles/sasskit-stylekit/ac-sasskit-config';
    @use '@amp/web-shared-styles/app/core/mixins/absolute-center' as *;
    @use '@amp/web-shared-styles/app/core/mixins/transgray-scrim' as *;
    @use 'ac-sasskit/core/helpers' as *;
    @use 'ac-sasskit/core/locale' as *;
    @use 'amp/stylekit/core/mixins/line-clamp' as *;

    .secondary-actions {
        display: flex;
        align-items: center;
        flex-direction: row;
        color: var(--systemSecondary);
        font: var(--title-3-short);

        :global(.chevron) {
            height: 16px;
            width: 16px;
            fill: var(--systemTertiary);

            @include rtl {
                transform: scaleX(-1);
            }
        }
    }

    .secondary-actions__icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
    }

    .shelf-list-item-classical {
        --artwork-override-height: var(--shelf-list-item-artwork-size);
        --artwork-override-width: var(--shelf-list-item-artwork-size);
        --nonPlatterOverrideIconColor: #fff;
        --playButtonIconHoverColor: #fff;
        display: contents;

        :global(.shelf-list-item) {
            padding-block: 0;
        }

        // Scale fallback SVG to artwork size
        :global(.artwork-fallback svg) {
            width: var(--artwork-override-width);
            height: var(--artwork-override-height);
        }

        :global(.shelf-list-item__title) {
            // Remove focus outline that wraps across lines
            :global(:focus-visible) {
                box-shadow: none;
            }

            // Apply focus outline around entire title box
            &:has(:focus-visible) {
                @include focus-outline;
            }
        }
    }

    .shelf-list-item-classical--track {
        --shelf-list-item-artwork-size: 40px;
        --linkHoverTextDecoration: underline;
        --contextMenuEllipsisFillOverride: var(--systemSecondary);

        :global(.shelf-list-item__artwork-wrapper) {
            margin-block: 7px;
        }
    }

    .shelf-list-item-classical--work {
        --shelf-list-item-artwork-size: 72px;

        :global(.shelf-list-item__artwork-wrapper) {
            margin-block: auto;
            margin-inline-end: 6px;
            pointer-events: none; // Forward pointer events to lockup
        }

        :global(.shelf-list-item__content) {
            height: 96px;
        }

        :global(.shelf-list-item__headline) {
            font: var(--subhead-emphasized);
        }

        :global(.shelf-list-item__title) {
            margin-top: 1px;
            font: var(--title-3-short);
        }
    }

    .shelf-list-item-classical--default {
        --shelf-list-item-artwork-size: 97px;
        --interactivePlayButtonSize: 100%;
        --playButtonSize: 30px;

        :global(.shelf-list-item__artwork-wrapper) {
            margin-block: auto;
            margin-inline-end: 10px;
        }

        :global(.play-button) {
            width: inherit;
            height: inherit;
        }

        :global(.shelf-list-item__title) {
            font: var(--title-3-short);

            &:hover {
                text-decoration: underline;
            }
        }

        :global(.shelf-list-item__subtitle) {
            margin-top: 2px;
        }

        :global(.shelf-list-item__headline) {
            font: var(--subhead-emphasized);
        }

        :global(.shelf-list-item__content) {
            height: 118px;
        }
    }

    // Set styles for Recordings footer lockup
    // spec: https://pd-hi.apple.com/viewvc/Common/Modules/Web/Classical/Headers%20%26%20Footers/Recording%20Header%20&%20Footer.png
    .shelf-list-item-classical--from-album {
        --shelf-list-item-artwork-size: 102px;

        :global(.shelf-list-item) {
            max-width: 500px;
            width: 100%;
        }

        :global(.shelf-list-item__artwork-wrapper) {
            margin-inline-end: 8px;
        }

        :global(.shelf-list-item__subtitle) {
            margin-top: 0;
            font: var(--body);
        }

        :global(.shelf-list-item__secondary-actions) {
            @media (--sidebar-visible) {
                display: none;
            }
        }
    }

    .shelf-list-item__header {
        :global(.click-action) {
            display: flex;
            gap: 2px;
            max-width: 100%;
        }

        .shelf-list-item__header-text {
            @include line-clamp;
        }

        // TODO: stick chevron to end when clamping
        :global(.chevron) {
            height: 10px;
            width: 10px;
            fill: var(--systemTertiary);
            align-self: center;
            flex-basis: 12px;

            @include rtl {
                transform: scaleX(-1);
            }
        }
    }

    .shelf-list-item__subtitle--button {
        display: inline;
        text-align: start;

        // Workaround for rdar://100779462 (Regression : Library albums with
        // multiple artists should show ellipsis in line2)
        //
        // `-webkit-line-clamp` fails when applied to <button>, but will still
        // apply to contents wrapped in <span>.
        > span {
            // `--lineClamp` property is set in <LineClamp> component
            @include line-clamp(var(--lineClamp));
        }
    }
</style>
